import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  KivuI18nContext,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import {
  getWelcomeAdVideoFallbackUrl,
  getWelcomeAdVideoUrl
} from "../../Utils/welcomeAdVideo";
import { KivuFMSLogo } from "../../assets";
import AnimatedContainer from "../common/AnimatedContainer";
import {
  authOrb1Sx,
  authOrb2Sx,
  welcomeBackgroundContainerSx,
  welcomeVideoSx,
  welcomeVideoWrapperSx
} from "../common/AuthSidePanel/authBackgroundStyles";
import {
  welcomeHeroContainerSx,
  welcomeMobileHeroBackgroundSx
} from "./welcomeSectionStyles";

const WelcomeHero: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { language } = useContext(KivuI18nContext);
  const { t } = useKivunovaTranslation();
  const [videoSrc, setVideoSrc] = useState(() =>
    getWelcomeAdVideoUrl(language)
  );

  useEffect(() => {
    if (isDesktop) {
      setVideoSrc(getWelcomeAdVideoUrl(language));
    }
  }, [language, isDesktop]);

  const handleVideoError = useCallback(() => {
    const fallback = getWelcomeAdVideoFallbackUrl();
    setVideoSrc((current) => (current === fallback ? current : fallback));
  }, []);

  return (
    <Box
      component="section"
      aria-label={t("welcome-hero-aria", "Welcome")}
      data-testid="welcome-hero"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        px: { xs: 2, sm: 3 },
        ...(isDesktop
          ? welcomeBackgroundContainerSx
          : welcomeMobileHeroBackgroundSx),
        ...welcomeHeroContainerSx
      }}
    >
      {isDesktop ? (
        <Box sx={welcomeVideoWrapperSx}>
          <Box
            component="video"
            key={videoSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            src={videoSrc}
            onError={handleVideoError}
            aria-hidden
            sx={welcomeVideoSx}
          />
        </Box>
      ) : (
        <>
          <Box sx={authOrb1Sx} />
          <Box sx={authOrb2Sx} />
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
              width: "100%"
            }}
          >
            <AnimatedContainer hoverEffect>
              <Box
                component="img"
                src={KivuFMSLogo}
                alt="Kivunova FMS Logo"
                sx={{
                  width: { xs: 100, sm: 110 },
                  height: "auto",
                  mb: 4,
                  filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.12))"
                }}
              />
            </AnimatedContainer>
            <AnimatedContainer
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.6,
                delay: 0.25,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                sx={{
                  color: "white",
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  letterSpacing: "-0.02em"
                }}
              >
                {t("welcome-to-ims-heading-text", "Welcome to Kivunova FMS")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 0,
                  color: "rgba(255,255,255,0.95)",
                  maxWidth: 420,
                  mx: "auto",
                  lineHeight: 1.6
                }}
              >
                {t(
                  "welcome-to-ims-intro-text",
                  "A simplified financial management to drive business growth and strategically scale operations"
                )}
              </Typography>
            </AnimatedContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WelcomeHero;
